'use strict'

/**
 * Pure management projections for the Master Lots overview and dashboard CSV.
 */

import {METAL_SYMBOLS, PGM_METALS} from '@/util/refinery/constants'
import {MASTER_LOT_STATUSES, MILESTONE_STATUSES} from '@/util/refinery/upstream/constants'
import {manifestUnitGrossToz} from './manifestWeights'
import {catalogEntriesFromManifest} from './manifestCatalog'
import {METAL_KEYS, summariseManifest} from './math'
import {assessContractReadiness} from './readiness'
import {isEligibleSourceLotStatus} from './sourceLotEligibility'
import {isSourceUnitReconciled} from './sourceLotReconciliation'

const ATTENTION_COMPLETE_STATUSES = new Set([
  MASTER_LOT_STATUSES.SETTLED,
  MASTER_LOT_STATUSES.CANCELLED,
])

/** Dave's assembly decision table — always all seven rows, zeros included. */
export const OVERVIEW_ELIGIBLE_METALS = Object.freeze([
  ...new Set([
    ...METAL_SYMBOLS.map((metal) => metal.toLowerCase()),
    ...PGM_METALS.map((metal) => metal.toLowerCase()),
  ]),
])

// Availability is eligible, unallocated fine ozt by Dave's seven-metal vector.
export const summarizeAvailableFineToz = (sourceLots) => {
  const totals = Object.fromEntries(OVERVIEW_ELIGIBLE_METALS.map((metal) => [metal, 0]))
  for (const lot of sourceLots || []) {
    if (!isEligibleSourceLotStatus(lot?.status)) continue
    for (const unit of lot.units || []) {
      if (unit?.allocatedMasterLotId != null) continue
      if (isSourceUnitReconciled(unit)) continue
      const weightToz = manifestUnitGrossToz(unit)
      if (weightToz <= 0) continue
      for (const metal of OVERVIEW_ELIGIBLE_METALS) {
        const purityPct = Number(unit?.purity?.[metal])
        // Missing / non-finite purity contributes 0 for that metal.
        if (!Number.isFinite(purityPct) || purityPct <= 0) continue
        totals[metal] += (weightToz * purityPct) / 100
      }
    }
  }
  return OVERVIEW_ELIGIBLE_METALS.map((metal) => ({metal, fineToz: totals[metal]}))
}

export const masterLotNeedsAttention = (lot) =>
  !ATTENTION_COMPLETE_STATUSES.has(lot?.status)

export const masterLotPartnerLabel = (lot) =>
  lot?.contract?.partner?.displayName || lot?.contract?.partner?.code || '(unknown refinery)'

export const masterLotContractLabel = (lot) =>
  lot?.contract?.title || lot?.contract?.id || ''

export const summarizeMasterLots = (masterLots) => {
  const statusCounts = new Map()
  let attentionCount = 0
  for (const lot of masterLots || []) {
    const status = lot?.status || 'unknown'
    statusCounts.set(status, (statusCounts.get(status) || 0) + 1)
    if (masterLotNeedsAttention(lot)) attentionCount += 1
  }
  return {
    statusCounts: [...statusCounts.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([status, count]) => ({status, count})),
    attentionCount,
  }
}

export const masterLotNextAction = (lot) => {
  if (lot?.status === MASTER_LOT_STATUSES.CONFIRMED) {
    const warningCount = Array.isArray(lot.readinessWarnings) ? lot.readinessWarnings.length : 0
    return warningCount
      ? `Ship / fulfill SO · ${warningCount} warning${warningCount === 1 ? '' : 's'}`
      : 'Ship / fulfill SO'
  }
  if (lot?.status === MASTER_LOT_STATUSES.DRAFT) {
    const readiness = readinessFor(lot)
    if (readiness.blockers.length) return `Resolve: ${readiness.blockers[0].message}`
    return readiness.warnings.length
      ? `Confirm & create SO · ${readiness.warnings.length} warning${readiness.warnings.length === 1 ? '' : 's'}`
      : 'Confirm & create SO'
  }
  if (lot?.status === MASTER_LOT_STATUSES.ASSAYED) return 'Enter settlement'
  if (!masterLotNeedsAttention(lot)) return 'No action'
  return 'Advance milestones'
}

export const projectDashboardLotRows = (masterLots) =>
  (masterLots || []).map((lot) => {
    const manifestSummary = summariseManifest(manifestUnits(lot))
    return {
      masterLotCode: lot.masterLotCode,
      partner: masterLotPartnerLabel(lot),
      contract: masterLotContractLabel(lot),
      status: lot.status,
      salesOrderTranId: lot.salesOrder?.tranId || '',
      grossWeightToz: lot.summary?.grossWeightToz ?? manifestSummary.grossWeightToz,
      milestoneProgress: masterLotMilestoneProgress(lot),
      nextAction: masterLotNextAction(lot),
      byMetal: Object.fromEntries(METAL_KEYS.map((metal) => [
        metal,
        {
          purityPct: manifestSummary.byMetal[metal]?.weightedAvgPurityPct ?? null,
          fineToz: manifestSummary.byMetal[metal]?.netPayableToz ?? 0,
        },
      ])),
    }
  })

const manifestUnits = (lot) => [
  ...(lot?.manifest?.sourceUnits || []),
  ...catalogEntriesFromManifest(lot?.manifest),
]

const readinessFor = (lot) => assessContractReadiness(manifestUnits(lot), lot?.contract)

export const masterLotMilestoneProgress = (lot) => {
  const milestones = Array.isArray(lot?.milestones) ? lot.milestones : []
  const complete = milestones.filter(({status}) => status === MILESTONE_STATUSES.COMPLETE).length
  return `${complete} / ${milestones.length}`
}
